import React, { useState } from 'react'
import { Card, CardHeader, Text, Button, makeStyles, tokens, mergeClasses } from '@fluentui/react-components'
import { ChevronDownRegular, ChevronUpRegular } from '@fluentui/react-icons'
import type { IFoldableSectionProps } from './types'
import { useEffects } from '../contexts/EffectsContext'

const useStyles = makeStyles({
	card: {
		marginBottom: '2rem',
		// Same themed surface as the skills matrix cards — readable in
		// both light and dark mode.
		backgroundColor: tokens.colorNeutralBackground1,
		border: '1px solid rgba(128, 128, 128, 0.5)',
		borderRadius: tokens.borderRadiusMedium,
		boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
		color: tokens.colorNeutralForeground1,
		':hover': {
			border: '1px solid rgba(128, 128, 128, 0.9)',
			boxShadow: '0 4px 12px rgba(32, 32, 32, 0.15)',
		}
	},
	header: {
		cursor: 'pointer',
		color: tokens.colorNeutralForeground1,
	},
	expandIcon: {
		color: tokens.colorNeutralForeground1,
	},
	headerIcon: {
		marginRight: '0.4em',
		verticalAlign: '-0.1em',
	},
	expandedContent: {
		marginBottom: '2rem', // Same margin as collapsed cards
		paddingTop: '1rem' // Small padding to separate from header
	}
})

export const FoldableSection: React.FC<IFoldableSectionProps> = ({ icon, title, description, children, isOpen, onToggle }) => {
	const styles = useStyles()
	const { effects } = useEffects()
	const [internalIsOpen, setInternalIsOpen] = useState(false)

	// Use controlled state if provided, otherwise use internal state
	const isExpanded = isOpen !== undefined ? isOpen : internalIsOpen
	const handleToggle = onToggle !== undefined ? onToggle : () => setInternalIsOpen(!internalIsOpen)

	// Cursor-following glow (gated by the 'Card hover glow' effect flag)
	const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
		const rect = event.currentTarget.getBoundingClientRect()
		event.currentTarget.style.setProperty('--mx', `${event.clientX - rect.left}px`)
		event.currentTarget.style.setProperty('--my', `${event.clientY - rect.top}px`)
	}

	const chevron = isExpanded
		? <ChevronUpRegular className={styles.expandIcon} />
		: <ChevronDownRegular className={styles.expandIcon} />

	return (
		<>
			<Card
				className={mergeClasses(styles.card, effects.cardGlow && 'spotlight-card')}
				onMouseMove={effects.cardGlow ? handleMouseMove : undefined}
			>
				<CardHeader
					onClick={handleToggle}
					className={styles.header}
					header={
						<Text size={800} weight="bold">
							<span className={styles.headerIcon}>{icon}</span>
							{title}
						</Text>
					}
					description={<Text>{description}</Text>}
					action={
						<Button
							appearance="transparent"
							aria-label={isExpanded ? `Collapse ${title} section` : `Expand ${title} section`}
							icon={chevron}
						/>
					}
				/>
			</Card>
			{isExpanded && (
				<div className={styles.expandedContent}>
					{children}
				</div>
			)}
		</>
	)
}
